import { motion } from 'framer-motion';
import { CheckCircle2, Users, Globe, Award, type LucideIcon } from 'lucide-react';

interface Metric {
  icon: LucideIcon;
  value: string;
  label: string;
}

const values: string[] = [
  'We ship production-ready infrastructure, not demos.',
  'Every solution is documented, observable, and transferable.',
  'We measure success by your outcomes—not our hours.',
  'Security and cost-efficiency are never afterthoughts.',
];

const metrics: Metric[] = [
  { icon: Users, value: '40+', label: 'Enterprise Clients' },
  { icon: Globe, value: '12', label: 'Countries Served' },
  { icon: Award, value: '8+', label: 'Years of Excellence' },
];

const tags: string[] = ['AWS Partner', 'AI-Certified', 'SOC 2 Ready', 'DevSecOps'];

export default function About() {
  return (
    <section id="about" className="relative py-24 lg:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs font-medium text-blue-400 uppercase tracking-widest mb-3">Who We Are</p>
            <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tight leading-tight mb-6">
              Engineering partners,
              <br />
              <span className="gradient-text">not order-takers.</span>
            </h2>
            <p className="text-slate-400 text-base leading-relaxed mb-6">
              Mataly Cloud Engineering was founded by AWS architects and AI engineers who got tired
              of watching companies waste millions on poorly designed cloud infrastructure.
              We built the firm we wished existed.
            </p>
            <p className="text-slate-400 text-base leading-relaxed mb-8">
              Today we serve startups and enterprise teams alike—designing the kind of scalable,
              secure, AI-augmented infrastructure that lets engineering teams focus on what matters:
              building great products.
            </p>

            <ul className="flex flex-col gap-3">
              {values.map((v) => (
                <li key={v} className="flex items-start gap-3">
                  <CheckCircle2 size={17} className="text-blue-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300 text-sm">{v}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="flex flex-col gap-4"
          >
            <div className="bento-card p-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-transparent" />
              <div className="relative z-10">
                <div className="text-5xl font-black gradient-text mb-3">Mataly</div>
                <div className="text-slate-400 text-sm leading-relaxed mb-6">
                  Our name is our promise—building mature, always-learning infrastructure for tomorrow's challenges.
                </div>
                <div className="flex gap-2 flex-wrap">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[11px] text-blue-400 border border-blue-500/20 bg-blue-500/5 px-3 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {metrics.map((m) => (
                <div key={m.label} className="bento-card p-5 flex flex-col items-center text-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-blue-600/10 flex items-center justify-center text-blue-400">
                    <m.icon size={16} />
                  </div>
                  <div className="text-2xl font-bold text-white">{m.value}</div>
                  <div className="text-[11px] text-slate-500">{m.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
